import { useState, useEffect } from 'react' // Импортируем хуки из React для управления состоянием и эффектами
import { SnowboardModel } from './types/snowboard' // Удаляем RentalBooking из импорта
import { snowboards } from './data/snowboards' // Импортируем массив доступных сноубордов
import { ModelCard } from './components/ModelCard' // Импортируем компонент для отображения карточек моделей
import { SizeSelector } from './components/SizeSelector' // Импортируем компонент для выбора размера
import { DateTimePicker } from './components/DateTimePicker' // Импортируем компонент для выбора даты и времени
import { DurationSelector } from './components/DurationSelector' // Импортируем компонент для выбора длительности
import './App.css' // Импортируем стили

// Основной компонент приложения
function App() {
  // Создаем состояния с помощью хука useState:
  const [selectedModel, setSelectedModel] = useState<SnowboardModel | null>(null) // Состояние для хранения выбранной модели сноуборда
  const [selectedSize, setSelectedSize] = useState<string | null>(null) // Состояние для хранения выбранного размера
  const [startDate, setStartDate] = useState<Date | null>(null) // Состояние для хранения даты начала аренды
  const [duration, setDuration] = useState<number | null>(null) // Состояние для хранения длительности аренды

  const handleBooking = () => {
    if (selectedModel && selectedSize && startDate && duration) {
      const bookingData = {
        modelName: selectedModel.name,
        modelId: selectedModel.id,
        size: selectedSize,
        startDate: startDate.toISOString(),
        endDate: new Date(startDate.getTime() + duration * 24 * 60 * 60 * 1000).toISOString(),
        duration: duration,
        userId: window.Telegram.WebApp.initDataUnsafe?.user?.id || ''
      }

          // Проверяем размер данных
    const dataString = JSON.stringify(bookingData);
    if (dataString.length > 4096) {
      window.Telegram.WebApp.showPopup({
        title: 'Ошибка',
        message: 'Данные слишком велики для отправки'
      });
      return;
    }

    try {
      // Отправляем данные через Telegram WebApp
      window.Telegram.WebApp.sendData(dataString)
    } catch (error) {
      console.error('Booking error:', error)
      window.Telegram.WebApp.showPopup({
        title: 'Ошибка',
        message: 'Не удалось отправить данные бронирования'
      })
    }
    }
  }

  // Добавляем обработчик для MainButton
  useEffect(() => {
    // Проверяем что мы НЕ в конфигураторе
    if (window.location.pathname.includes('/configurator')) return;

    window.Telegram.WebApp.onEvent('mainButtonClicked', handleBooking)

    return () => {
      window.Telegram.WebApp.offEvent('mainButtonClicked', handleBooking)
    }
  }, [selectedModel, selectedSize, startDate, duration])

  // Управляем видимостью MainButton
  useEffect(() => {
    // Проверяем что мы НЕ в конфигураторе
    if (window.location.pathname.includes('/configurator')) return;

    if (selectedModel && selectedSize && startDate && duration) {
      window.Telegram.WebApp.MainButton.setText('Забронировать')
      window.Telegram.WebApp.MainButton.show()
    } else {
      window.Telegram.WebApp.MainButton.hide()
    }
  }, [selectedModel, selectedSize, startDate, duration])

  // Функция обработки выбора модели сноуборда
  const handleModelSelect = (model: SnowboardModel) => {
    setSelectedModel(model)
    // При выборе новой модели сбрасываем выбранный размер
    setSelectedSize(null)
  }

  // Функция обработки выбора размера сноуборда
  const handleSizeSelect = (size: string) => {
    setSelectedSize(size)
  }

  // Возвращаем JSX разметку компонента
  return (
    <div className="container">
      {/* Контейнер с логотипом */}
      <div className="logo-container">
        <a href="https://salutmfg.co/" target="_blank" rel="noopener noreferrer">
          <img src="./images/salut-logo.svg" alt="Salut Logo" className="logo" />
        </a>
      </div>
      {/* Заголовок приложения */}
      <h1>Snowboard Rental</h1>

      {/* Секция с моделями сноубордов */}
      <section className="models-section">
        <div className="models-header">
          <h2>Выберите модель</h2>
        </div>
        <div className="models-container">
          {/* Отображаем карточку для каждой модели сноуборда */}
          {snowboards.map(model => (
            <ModelCard
              key={model.id}
              model={model}
              isSelected={selectedModel?.id === model.id}
              onSelect={handleModelSelect}
            />
          ))}
        </div>
      </section>

      {/* Секция выбора размера (отображается только после выбора модели) */}
      {selectedModel && (
        <section className="size-section">
          <h2>Выберите размер</h2>
          <SizeSelector
            sizes={selectedModel.sizes}
            selectedSize={selectedSize}
            onSelect={handleSizeSelect}
          />
        </section>
      )}

      {/* Секция выбора даты и длительности (отображается только после выбора размера) */}
      {selectedSize && (
        <section className="datetime-section">
          <h2>Выберите период аренды</h2>
          {/* Компонент выбора длительности аренды */}
          <DurationSelector
            selectedDuration={duration}
            onSelect={setDuration}
          />
          {/* Компонент выбора даты (отображается после выбора длительности) */}
          {duration && (
            <DateTimePicker
              startDate={startDate}
              duration={duration}
              onStartDateChange={setStartDate}
            />
          )}
        </section>
      )}
    </div>
  )
}

// Экспортируем компонент для использования в других частях приложения
export default App